import {useCallback} from 'react';

import * as RunHooks from '../../../state/runs/hooks';
import {WBTableRowWithRunSetInfo} from '../../../util/tablecols';

type UseTagEditorOptions = {
  entityName: string;
  isGroup: boolean;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
};

/**
 * A hook that provides two fns for adding and removing tags from a run or run
 * group.
 *
 * @param entityName
 * @param isGroup
 * @param projectName
 * @param row
 */
export function useRunTagEditor({
  entityName,
  isGroup,
  projectName,
  row,
}: UseTagEditorOptions) {
  const updateRunTags = RunHooks.useUpdateRunTags();
  const updateGroupRunTags = RunHooks.useUpdateGroupRunTags(
    projectName,
    entityName
  );

  const updater = isGroup ? updateGroupRunTags : updateRunTags;

  const addTag = useCallback(
    (tagName: string) => {
      const currentTagNames = row.tags.map(t => t.name);
      if (currentTagNames.includes(tagName)) {
        return; // Tag already exists, do nothing
      }
      updater(row, [...currentTagNames, tagName]);
    },
    [row, updater]
  );

  const deleteTag = useCallback(
    (tagName: string) => {
      const currentTagNames = row.tags.map(t => t.name);
      if (!currentTagNames.includes(tagName)) {
        return; // Tag does not exist, do nothing
      }

      updater(
        row,
        currentTagNames.filter(t => t !== tagName)
      );
    },
    [row, updater]
  );

  return {
    addTag,
    deleteTag,
  };
}
